import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { getCheckoutStatus, gbp } from "../lib/api";
import { useCart } from "../context/CartContext";

const POLL_MAX = 6;
const POLL_INTERVAL = 2000;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | success | error | timeout
  const [order, setOrder] = useState(null);
  const { clearCart } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (!active) return;
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setState("success");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= POLL_MAX) {
          setState("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        if (!active) return;
        setState("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, clearCart]);

  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-6 py-24" data-testid="checkout-success-page">
      <div className="max-w-lg w-full text-center">
        <Link to="/" className="inline-flex flex-col leading-none mb-12">
          <span className="font-sans font-black tracking-[0.14em] text-lg text-ink">PRESTIGE</span>
          <span className="font-serif italic text-sm tracking-[0.28em] text-faded">wellness co.</span>
        </Link>

        {state === "polling" && (
          <div className="flex flex-col items-center gap-5">
            <Loader2 className="w-10 h-10 text-forest animate-spin" strokeWidth={1.5} />
            <p className="font-serif italic text-2xl text-faded">Confirming your payment…</p>
          </div>
        )}

        {state === "success" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 rounded-full bg-forest text-cream flex items-center justify-center mx-auto mb-8">
              <Check className="w-8 h-8" strokeWidth={2.5} />
            </div>
            <h1 className="font-sans font-black tracking-tight text-4xl md:text-5xl text-ink" data-testid="success-heading">Order confirmed.</h1>
            <p className="mt-4 font-serif italic text-xl text-faded">Your ritual is on its way. A confirmation has been sent to your email.</p>

            {order && (
              <div className="mt-10 text-left rounded-2xl border border-tan bg-offwhite p-6" data-testid="order-summary">
                <p className="text-xs uppercase tracking-[0.2em] text-faded mb-4">Order summary</p>
                <ul className="space-y-3">
                  {order.items?.map((it, i) => (
                    <li key={i} className="flex justify-between text-sm">
                      <span className="text-ink">{it.quantity}× {it.name} <span className="text-faded">({it.plan === "subscribe" ? "Subscribe" : "One-time"})</span></span>
                      <span className="font-bold text-ink">{gbp(it.line_total)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-4 border-t border-tan flex justify-between">
                  <span className="font-serif italic text-faded">Total</span>
                  <span className="font-sans font-black text-xl text-ink">{gbp(order.amount)}</span>
                </div>
              </div>
            )}

            <Link to="/" data-testid="continue-shopping" className="group mt-10 inline-flex items-center gap-3 rounded-full bg-forest text-cream px-8 py-4 text-sm font-bold tracking-widest uppercase transition-colors hover:bg-forest-deep">
              Continue shopping
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
            </Link>
          </motion.div>
        )}

        {(state === "timeout" || state === "error") && (
          <div className="flex flex-col items-center gap-5">
            <p className="font-serif italic text-2xl text-faded">
              {state === "timeout"
                ? "Still processing — check your email for confirmation shortly."
                : "We couldn't confirm this payment."}
            </p>
            <Link to="/" className="text-sm font-bold uppercase tracking-widest text-forest underline underline-offset-4">Back to shop</Link>
          </div>
        )}
      </div>
    </div>
  );
}
